<template>
  <div v-if="false"></div>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import { every, groupBy, map } from "lodash";
import moment from "moment";

export default {
  computed: {
    secFunctionInputList() {
      const role = this.role;
      // 1. Security Functions for Primary Role

      // Retrieve list of primary role and function associations for this organization role.
      const secFunctionPrimaryRoleList =
        this.filterPrimaryRoleFunctionListByAttribute(
          "secRolePrimaryId",
          role.secRolePrimaryId
        );

      // Extract a list of secFunctionId values from the list of primary roles and associated security functions.
      const secFunctionIdListForPrimaryRole = secFunctionPrimaryRoleList.map(
        item => {
          return {
            secFunctionId: item.secFunctionId,
            inverted: item.inverted,
          };
        }
      );

      // 2. Security Functions for Organization Role

      // Retrieve list of organization role and answer associations.
      const organizationRoleAnswerList =
        this.filterOrganizationRoleAnswerListByAttribute(
          "secRoleOrganizationId",
          role.id
        );

      // Create list of secFunctionId values using secAnswerId values from organization role and answer associations.
      let secFunctionIdListForOrgRoleAnswer = [];
      if (organizationRoleAnswerList.length) {
        organizationRoleAnswerList.forEach(item => {
          const answerFunctionList = this.filterAnswerFunctionListByAttribute(
            "secAnswerId",
            item.secAnswerId
          );
          const secFunctionIdList = answerFunctionList.map(item => {
            return {
              secFunctionId: item.secFunctionId,
              inverted: item.inverted,
            };
          });

          secFunctionIdListForOrgRoleAnswer =
            secFunctionIdListForOrgRoleAnswer.concat(secFunctionIdList);
        });
      }

      // 3. Generate a list of function inputs based on primary role base functions and the organization's answers.

      // Concat the two arrays of secFunctionId values (secFunctionIdListForPrimaryRole and secFunctionIdListForOrgRoleAnswer).
      const secFunctionIdListAll = secFunctionIdListForOrgRoleAnswer.concat(
        secFunctionIdListForPrimaryRole
      );

      // Deduplicate concatenated array.
      const dedupedFunctionIdList = [...new Set(secFunctionIdListAll)];

      // Create and return a function input list to be used for matching security templates.
      let functionInputList = [];
      dedupedFunctionIdList.forEach(item => {
        const functionInput = this.filterFunctionInputListByAttribute(
          "secFunctionId",
          item.secFunctionId
        );
        // Apply association-based invert attribute to individual input
        if (item.inverted) {
          functionInput.forEach(input => (input.inverted = true));
        }
        functionInputList = functionInputList.concat(functionInput);
      });
      return functionInputList;
    },
    serviceAreaId() {
      return this.organizationRecord.serviceAreaId;
    },
    ...mapGetters({
      filterAnswerByAttribute: "secAnswer/filterByAttributeValue",
      filterAnswerFunctionListByAttribute:
        "secAnswerSecFunction/filterByAttributeValue",
      filterFunctionInputListByAttribute:
        "secFunctionInput/filterByAttributeValue",
      filterFunctionListByAttribute: "secFunction/filterByAttributeValue",
      filterOperatorList: "operator/filterByAttributeValue",
      filterOrganizationRoleAnswerByAttribute:
        "secRoleOrganizationSecAnswer/filterByAttributeValue",
      filterOrganizationRoleAnswerListByAttribute:
        "secRoleOrganizationSecAnswer/filterByAttributeValue",
      filterPrimaryRoleFunctionListByAttribute:
        "secRolePrimarySecFunction/filterByAttributeValue",
      filterPrimaryRoleQuestionListByAttribute:
        "secRolePrimarySecQuestion/filterByAttributeValue",
      getAnswerById: "secAnswer/getById",
      getQuestionById: "secQuestion/getById",
      getOrganizationRoleById: "secRoleOrganization/getById",
      getTemplateById: "template/getById",
      getUserById: "user/getById",
    }),
    ...mapState({
      organizationId: state => state.session.organizationId,
      organizationRecord: state => state.organization.record,
      securityClassList: state => state.securityClass.list,
      templateList: state => state.template.list,
      userId: state => state.session.user.userId,
    }),
  },
  async created() {
    await this.initData();
  },

  data() {
    return {
      classTypeMap: {
        Analytics: "analyticsSecurityClass",
        Bridges: "bridgesSecurityClass",
        Cadence: "cadenceSecurityClass",
        "Care Everywhere": "careEverywhereSecurityClass",
        Epiccare: "epiccareSecurityClass",
        Identity: "identitySecurityClass",
        "In Basket": "inBasketSecurityClass",
        "Infection and Isolation": "infectionAndIsolationSecurityClass",
        MyChart: "myChartSecurityClass",
        Prelude: "preludeSecurityClass",
        "Professional Billing": "professionalBillingSecurityClass",
        Radar: "radarSecurityClass",
        Referral: "referralSecurityClass",
        "Reporting Workbench": "reportingWorkbenchSecurityClass",
        "Release of Information": "roiSecurityClass",
        Shared: "sharedSecurityClass",
        "Web Suite": "webSuiteSecurityClass",
        Willow: "willowSecurityClass",
      },
      role: null,
    };
  },
  methods: {
    async createSnapshot({ answerMap, secRoleOrganizationId }) {
      try {
        await this.initData();
      } catch (e) {
        console.error(e);
        this.$notifyError({
          message: "Could not refresh data before snapshot",
        });
        return;
      }
      this.role = await this.getOrganizationRoleById(secRoleOrganizationId);

      const matchedTemplateList = this.filterTemplateList();
      const matchedTemplateListIgnoreGroupers = this.filterTemplateList({
        ignoreGroupers: true,
      });

      const questionAnswerMap = map(answerMap, (value, key) => {
        return {
          question: this.getQuestionById(parseInt(key, 10)).text,
          answer: this.getAnswerById(value).text,
        };
      });

      const snapshot = JSON.stringify({
        created: moment.utc().format(),
        matchedTemplateList,
        matchedTemplateListIgnoreGroupers,
        questionAnswerMap,
      });

      return this.updateOrganizationRole([secRoleOrganizationId, { snapshot }]);
    },
    filterTemplateList(optionMap = { ignoreGroupers: false }) {
      const functionInputList = this.secFunctionInputList;
      const groupedFunctionInputList = groupBy(functionInputList, "type");

      const matchedClassListMapByType = {};
      if (functionInputList.length) {
        /* 
          Create a mapping to check within the loop for whether the filters are all exclusive (i.e. "not contains"
          or inverted "contains"). If true, then make security templates that have no securityClassId for the security
          class type pass, instead of the hard fail
        */
        const securityClassTypeOnlyExclusiveMap = {};

        // Iterates over each list of function inputs grouped by the application type
        for (let [key, typeList] of Object.entries(groupedFunctionInputList)) {
          securityClassTypeOnlyExclusiveMap[this.classTypeMap[key]] = every(
            typeList,
            input => {
              if (
                (input.inverted && input.operator !== "contains") ||
                (!input.inverted && input.operator === "contains")
              ) {
                return false;
              }
              return true;
            }
          );
          // Classes are filtered on whether all the function inputs' items are found
          // and operators and values resolve
          const matchedClassList = this.securityClassList.filter(
            securityClass => {
              const { itemList } = securityClass;

              const findResults = every(
                typeList,
                input => {
                  const matchedItem = itemList.find(
                    item => item.item === input.point
                  );
                  if (!matchedItem) {
                    return false;
                  }

                  const { callback } = this.filterOperatorList(
                    "name",
                    input.operator
                  )[0];

                  const { valueList } = matchedItem;
                  const outcome = callback(valueList, input.value);

                  // Reverse outcomes for inverted input associations
                  if (input.inverted) return !outcome;

                  return outcome;
                },
                true
              );

              return findResults;
            }
          );

          const matchedClassIdList = matchedClassList.map(item => item.id);

          // The list of matched classIds are added to th respective templateSecurityClass name
          matchedClassListMapByType[this.classTypeMap[key]] =
            matchedClassIdList;
        }

        return this.templateList.filter(item => {
          // Ensure each defined matching class list contains the security point
          const allClassesMatch = every(
            matchedClassListMapByType,
            (classList, key) => {
              const templateClassValue = item[key];
              if (
                !templateClassValue &&
                securityClassTypeOnlyExclusiveMap[key]
              ) {
                return true;
              } else if (!templateClassValue) {
                return false;
              }

              if (Array.isArray(classList) && classList.length) {
                return classList.includes(templateClassValue);
              }
              return false;
            }
          );

          if (!allClassesMatch) return false;
          const { grouper } = item;
          if (grouper) {
            if (optionMap.ignoreGroupers === true) {
              return true;
            }

            const [grouperType, grouperValue] = grouper.split(":");
            if (grouperType === "ACCESS") {
              return (
                grouperValue === "NONE" ||
                grouperValue.split(",").includes(`${this.serviceAreaId}`)
              );
            } else if (grouperType === "EXCEPT") {
              return !grouperValue.split(",").includes(`${this.serviceAreaId}`);
            }
          }
          return false;
        });
      }
    },
    async initData() {
      return Promise.all([
        this.fetchAnswerFunctionList({ reload: true }),
        this.fetchFunctionInputList({ reload: true }),
        this.fetchOrganizationRoleAnswerList(),
        this.fetchPrimaryRoleFunctionList({ reload: true }),
        this.fetchPrimaryRoleQuestionList({ reload: true }),
        this.fetchSecurityClassList({ reload: true }),
        this.fetchTemplateList({ reload: true }),
        this.fetchUserList({ reload: true }),
        this.getOrganizationById(this.organizationId),
      ]);
    },
    ...mapActions({
      fetchAnswerFunctionList: "secAnswerSecFunction/list",
      fetchFunctionInputList: "secFunctionInput/list",
      fetchOrganizationRoleAnswerList: "secRoleOrganizationSecAnswer/list",
      fetchPrimaryRoleFunctionList: "secRolePrimarySecFunction/list",
      fetchPrimaryRoleQuestionList: "secRolePrimarySecQuestion/list",
      fetchUserList: "user/list",
      fetchRoleList: "secRoleOrganization/list",
      fetchSecurityClassList: "securityClass/list",
      fetchTemplateList: "template/list",
      getOrganizationById: "organization/getByOrganizationId",
      updateOrganizationRole: "secRoleOrganization/update",
    }),
  },
};
</script>
